// * Importing necessary modules
import { useState, useEffect, useCallback, useMemo, FormEvent } from "react";

//* Importing necessary react-router-dom modules
import { useNavigate, useParams } from "react-router-dom";

// * Importing necessary react-bootstrap modules
import { Form, InputGroup } from "react-bootstrap";

// * Importing necessary react-select modules
import AsyncSelect from "react-select/async";

// * Importing necessary react-icons modules
import { RiArrowGoBackLine } from "react-icons/ri";

// * Importing necessary moment modules
import moment from "moment";

// * Importing necessary components
import DefaultButton from "../../../../component/base/DefaultButton";
import { Attention } from "../../../../component/base/Attention";
import { Searchbar } from "../../../../component/list/searchbar";
import { Actions } from "../../../../component/list/actions";

// * Importing necessary hooks
import { useDiagrams } from "../../../../hooks/diagrams/useDiagrams";
import { useApplicationDiagrams } from "../../../../hooks/applications/applicationDiagrams/useApplicationDiagrams";

var debounceFn = require("lodash.debounce");

export function ApplicationManagerDiagramForm(props: { editMode?: boolean }) {
  const params = useParams();
  const applicationId = params.id || "";
  const token = localStorage.getItem("token") || "";

  // HOOK para gerenciar diagramas disponíveis
  const {
    diagrams,
    loading: loadingDiagrams,
    error: errorDiagrams,
    setSearch,
  } = useDiagrams(token);

  // HOOK para gerenciar diagramas já vinculados à aplicação
  const {
    applicationDiagrams,
    loading: loadingAppDiagrams,
    error: errorAppDiagrams,
    createApplicationDiagram,
    deleteApplicationDiagram,
  } = useApplicationDiagrams(applicationId, token);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [searchForm, setSearchForm] = useState<string>("");
  const [diagramsSelected, setDiagramsSelected] = useState<Record<string, any>>(
    {}
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [diagramText, setDiagramText] = useState<string>("");

  const navigate = useNavigate();

  // Converte os registros vindos do backend (applicationDiagrams) para o estado local
  useEffect(() => {
    if (!applicationDiagrams) return;
    const selectedObj: Record<string, any> = {};
    applicationDiagrams.forEach((item) => {
      if (item.diagram) {
        const { id, pageTitle, createdAt } = item.diagram;
        selectedObj[id] = { id, pageTitle, createdAt };
      }
    });
    setDiagramsSelected(selectedObj);
  }, [applicationDiagrams]);

  // Adiciona um diagrama ao estado local, evitando duplicatas
  const handleAddDiagram = useCallback((diagram: any) => {
    setDiagramsSelected((prev) => {
      // Se o diagrama já foi adicionado, retorna o estado atual sem alterações
      if (prev[diagram.id]) {
        return prev;
      }
      return {
        ...prev,
        [diagram.id]: diagram,
      };
    });
  }, []);

  // Remove um diagrama do estado local
  const handleRemoveDiagram = useCallback((diagramId: string) => {
    setDiagramsSelected((prev) => {
      const newState = { ...prev };
      delete newState[diagramId];
      return newState;
    });
  }, []);

  // Filtra localmente a lista de diagramas disponíveis
  const searchDiagrams = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      setSearch(inputValue);
      const filtered = diagrams.filter((d: any) =>
        d.pageTitle.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filtered);
    },
    [diagrams, setSearch]
  );

  // Debounce para evitar chamadas excessivas
  const debouncedSearchDiagrams = useMemo(
    () => debounceFn(searchDiagrams, 250),
    [searchDiagrams]
  );

  // Filtro memoizado dos diagramas que estão selecionados
  const filteredDiagramsSelected = useMemo(() => {
    const lowerSearch = searchForm.toLowerCase();
    return Object.values(diagramsSelected).filter((item: any) =>
      item.pageTitle.toLowerCase().includes(lowerSearch)
    );
  }, [diagramsSelected, searchForm]);

  const handleSave = useCallback(async () => {
    try {
      setLoadingSave(true);

      // Obter os IDs dos diagramas já vinculados (usando o ID do diagrama, não o da ligação)
      const existingDiagramIds = applicationDiagrams.map(
        (appDiagram) => appDiagram.diagram?.id
      );
      const selectedIds = Object.keys(diagramsSelected);

      // Adiciona os diagramas que ainda não estão vinculados
      for (const id of selectedIds) {
        if (!existingDiagramIds.includes(id)) {
          await createApplicationDiagram({ diagram_id: id });
        }
      }

      // Remove os diagramas que foram desmarcados
      for (const appDiagram of applicationDiagrams) {
        if (!selectedIds.includes(appDiagram.diagram?.id)) {
          await deleteApplicationDiagram(appDiagram.id);
        }
      }

      navigate(`/tests/view/${applicationId}?tab=3`);
    } catch (error) {
      console.error("Erro ao salvar diagramas:", error);
      setErrorMessage("Ocorreu um erro ao salvar os diagramas.");
    } finally {
      setLoadingSave(false);
    }
  }, [
    applicationDiagrams,
    diagramsSelected,
    applicationId,
    navigate,
    createApplicationDiagram,
    deleteApplicationDiagram,
  ]);


  // Submit do formulário principal
  const onSubmitForm = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      void handleSave();
    },
    [handleSave]
  );

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body" style={{ overflow: "visible" }}>
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              Adicionar/Remover Diagramas
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              Cadastre/Edite os diagramas da aplicação
            </p>
            <br />

            {/* Exibe erros de API ou erros locais */}
            {(errorAppDiagrams || errorDiagrams || errorMessage) && (
              <div style={{ display: "flex", marginBottom: 15, width: "100%" }}>
                <div className="alert alert-danger w-100 mb-0" role="alert">
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                    onClick={() => setErrorMessage(null)}
                  ></button>
                  <p style={{ marginBottom: 0, whiteSpace: "pre-line" }}>
                    {errorMessage || errorAppDiagrams || errorDiagrams}
                  </p>
                </div>
              </div>
            )}

            {/* Loading geral */}
            {(loadingAppDiagrams || loadingDiagrams) && (
              <div className="row mt-2">
                <div
                  className="col-12 d-flex justify-content-center align-items-center"
                  style={{ marginTop: "2rem" }}
                >
                  <div className="spinner-border text-primary" role="status" />
                </div>
              </div>
            )}

            {/* Conteúdo principal */}
            {!loadingAppDiagrams && !loadingDiagrams && (
              <Form onSubmit={onSubmitForm} style={{ position: "relative" }}>
                <InputGroup>
                  <div className="flex fdcolumn flex1 margin-bottom">
                    <Form.Label>
                      Diagrama <Attention />
                    </Form.Label>

                    <AsyncSelect
                      getOptionLabel={(option: any) => option.pageTitle}
                      getOptionValue={(option: any) => option.id}
                      placeholder="Digite parte do nome do diagrama..."
                      noOptionsMessage={() => "Nenhuma opção encontrada"}
                      onChange={(selected: any) => {
                        if (selected) {
                          handleAddDiagram(selected);
                        }
                      }}
                      onInputChange={(inputValue: string) => {
                        setDiagramText(inputValue);
                        return inputValue;
                      }}
                      cacheOptions
                      loadOptions={debouncedSearchDiagrams}
                      defaultOptions={diagrams}
                      value={null} // Não fixa a seleção
                    />
                  </div>
                </InputGroup>

                <br />

                <Form.Label>
                  Diagramas <Attention />
                </Form.Label>
                <div className="row">
                  <div className="col-12">
                    <Searchbar
                      search_input_value={searchForm}
                      search_text="Encontre um diagrama"
                      onChangeSearchInput={(value: any) => setSearchForm(value)}
                      onSearchButtonClick={() => {}}
                    />

                    <div className="table-rep-plugin">
                      <div
                        className="table-responsive mb-0"
                        data-pattern="priority-columns"
                      >
                        <table
                          id="diagrams-table"
                          className="table table-striped"
                        >
                          <thead>
                            <tr>
                              <th>Nome</th>
                              <th>Descrição</th>
                              <th>Criado em</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredDiagramsSelected.map((item: any) => (
                              <tr key={item.id}>
                                <td>{item.pageTitle}</td>
                                <td>{item.description || "-"}</td>
                                <td>
                                  {moment(item.createdAt).format(
                                    "DD/MM/YYYY HH:mm"
                                  )}
                                </td>
                                <td>
                                  <Actions
                                    actionItem={item}
                                    onDeleteClick={() =>
                                      handleRemoveDiagram(item.id)
                                    }
                                    hideManager
                                    hideEdit
                                    hideDelete={false}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {filteredDiagramsSelected.length === 0 && (
                      <div className="row w-100 mt-3">
                        <div className="col-12">
                          <p style={{ textAlign: "center" }}>
                            Nenhum diagrama vinculado/encontrado
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <br />

                {/* Botões de salvar ou voltar */}
                {!loadingSave && (
                  <div className="w100 inline-flex jcsb mt-2">
                    <DefaultButton
                      bg="secondary"
                      text="Voltar"
                      icon={<RiArrowGoBackLine />}
                      to={`/tests/view/${applicationId}`}
                    />

                    <DefaultButton
                      type="submit"
                      bg="confirm"
                      text="Salvar diagramas"
                      disabled={loadingSave}
                      loadingText="Salvando diagramas"
                      loading={loadingSave}
                    />
                  </div>
                )}

                {loadingSave && (
                  <div className="row mt-2">
                    <div className="col-12 d-flex justify-content-center align-items-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      />
                    </div>
                  </div>
                )}
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
